// ═══════════════════════════════════════════════════════════════════════════
// ForgeTrack — Public Investor Share Page
// Route: /share/$token
// apps/web/src/routes/share.$token.tsx
//
// Public route — no authentication required. Loads investor dashboard data
// via share token. Uses the same InvestorDashboardView component as the
// authenticated /investor page.
// ═══════════════════════════════════════════════════════════════════════════

import { createFileRoute } from '@tanstack/react-router';
import { ENGINE_COLORS as C, ENGINE_FONTS as F, ENGINE_KEYFRAMES as K } from '../components/engine/tokens';
import { GlassPanel, EmptyState, LoadingShimmer, PulseDot } from '../components/engine/EnginePageShell';
import { useClock } from '../components/engine/hooks';
import { usePublicInvestorDashboard } from '../lib/api/investor';
import { InvestorDashboardView } from './investor';

export const Route = createFileRoute('/share/$token')({
  component: PublicSharePage,
});

function PublicSharePage() {
  const { token } = Route.useParams();
  const { data, loading, error } = usePublicInvestorDashboard(token);
  const clock = useClock();

  return (
    <div style={{
      minHeight: '100vh',
      background: C.bg,
      fontFamily: F.display,
      color: C.text,
      position: 'relative',
      overflow: 'hidden',
    }}>
      <style>{K}</style>

      {/* Neural grid */}
      <div style={{
        position: 'fixed', inset: 0,
        backgroundImage: `linear-gradient(${C.amber}06 1px, transparent 1px), linear-gradient(90deg, ${C.amber}06 1px, transparent 1px)`,
        backgroundSize: '60px 60px',
        animation: 'engine-neural 25s linear infinite',
        pointerEvents: 'none',
      }} />

      {/* Radial glow */}
      <div style={{
        position: 'fixed', top: 0, left: '50%', transform: 'translateX(-50%)',
        width: '120%', height: '400px',
        background: `radial-gradient(ellipse at 50% 0%, ${C.amber}10 0%, transparent 70%)`,
        pointerEvents: 'none',
      }} />

      {/* Top bar — minimal, investor-facing */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 50,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 24px',
        background: 'rgba(5,9,16,0.94)',
        backdropFilter: 'blur(12px)',
        borderBottom: `1px solid ${C.faint}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {data?.logoUrl && (
            <img src={data.logoUrl} alt="" style={{ height: 24, borderRadius: 4 }} />
          )}
          <span style={{ fontSize: 14, fontWeight: 600, color: C.text }}>
            {data?.customTitle || data?.projectName || 'Engineering Intelligence'}
          </span>
          {data?.companyName && (
            <span style={{ fontSize: 11, color: C.muted }}>· {data.companyName}</span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <PulseDot color={C.amber} size={6} />
          <span style={{ fontSize: 9, fontFamily: F.mono, letterSpacing: '0.12em', color: C.amber }}>
            INVESTOR VIEW
          </span>
          <span style={{ fontSize: 10, fontFamily: F.mono, color: C.dim }}>{clock}</span>
        </div>
      </div>

      {/* Content */}
      <div style={{ position: 'relative', zIndex: 1, maxWidth: 1400, margin: '0 auto', padding: '24px 24px 80px' }}>
        {loading ? (
          <>
            <GlassPanel delay={0.1} style={{ marginBottom: 20, height: 200 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 40, height: '100%', padding: 40 }}>
                <LoadingShimmer width={200} height={60} />
                <div style={{ flex: 1 }}><LoadingShimmer height={40} /></div>
              </div>
            </GlassPanel>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <GlassPanel delay={0.3}><LoadingShimmer height={200} /></GlassPanel>
              <GlassPanel delay={0.4}><LoadingShimmer height={200} /></GlassPanel>
            </div>
          </>
        ) : error ? (
          <GlassPanel accent={C.coral} delay={0.1}>
            <EmptyState
              icon="🔒"
              title="Dashboard unavailable"
              subtitle={error.message || 'This share link may have expired or been revoked.'}
            />
          </GlassPanel>
        ) : data ? (
          <InvestorDashboardView data={data} />
        ) : null}
      </div>

      {/* Bottom bar */}
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 50,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '8px 24px',
        background: 'rgba(5,9,16,0.95)',
        borderTop: `1px solid ${C.faint}`,
        fontSize: 9, fontFamily: F.mono, letterSpacing: '0.1em', color: C.dim,
      }}>
        <span>R&D Intelligence by ForgeTrack</span>
        <span>forgetrack.io · Powered by skipLogic™</span>
      </div>
    </div>
  );
}
